/**
 * 플랜 회고. 스코어카드는 기준선·비용으로 계산하고, KPT는 이 플랜에 남긴다.
 */
import { Caption1, Checkbox, makeStyles, mergeClasses, Textarea, tokens } from "@fluentui/react-components";
import { Print16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton, FuiInput } from "@/components/fui-control";
import { ListToolbar } from "@/components/list-toolbar";
import { SectionTitle } from "@/components/ui-typography";
import type { KptCard } from "@/engine/types";
import { useCan, usePlanSurface } from "@/features/access/use-plan-access";
import { buildScorecard, kptAuthorLabel, promotedLessons, retroAccess, retroPhase } from "@/features/retrospective/retrospective-model";
import { pageMax, pageStackAt, contentShell, shellGutter } from "@/shell/content-frame";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";

const COLUMNS: { id: KptCard["column"]; label: string }[] = [
  { id: "keep", label: "유지" },
  { id: "problem", label: "문제" },
  { id: "try", label: "시도" },
];

const PRINT = `@media print { @page { size: A4; margin: 12mm; } [data-print-hide] { display: none !important; } }`;

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
  },
  note: {
    color: tokens.colorNeutralForeground2,
  },
  board: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalM,
    [pageMax(pageStackAt)]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "6px 0",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  count: {
    marginLeft: "auto",
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground2,
  },
  composer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  summary: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    maxWidth: "720px",
  },
  value: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
});

export function ProjectReviewPage() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const project = useProjectStore((s) => s.project);
  const surface = usePlanSurface();
  const canArchive = useCan("plan.archive");
  const canEdit = useCan("plan.schedule.edit");
  const canExport = useCan("plan.export");
  const phase = retroPhase(project);
  const access = retroAccess(phase);
  const locked = access === "readonly" || surface.readOnly;
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const score = useMemo(() => buildScorecard(project), [project]);
  const cards = project.retrospective?.cards ?? [];
  const promoted = promotedLessons(cards);
  const [summary, setSummary] = useState(project.retrospective?.summary ?? "");

  useEffect(() => {
    setSummary(project.retrospective?.summary ?? "");
  }, [project.id, project.retrospective?.summary]);

  return (
    <div className={styles.page}>
      <style>{PRINT}</style>
      <div data-print-hide="">
        <ListToolbar
          title="회고"
          description={phase === "done" ? "완료된 플랜" : phase === "archived" ? "보관된 플랜" : "진행 중인 플랜"}
          headingAction={
            <div className={styles.actions} data-print-hide="">
              {canExport ? (
                <FuiButton
                  appearance="subtle"
                  onClick={() => {
                    const id = useCatalogStore.getState().saveProjectTemplate(project);
                    if (!id) return;
                    setSaved(true);
                    window.setTimeout(() => setSaved(false), 2000);
                  }}
                >
                  템플릿으로 저장
                </FuiButton>
              ) : null}
              {access === "draft" && canEdit ? (
                <FuiButton appearance="primary" onClick={() => useProjectStore.getState().setLifecycle("done")}>
                  완료로 전환
                </FuiButton>
              ) : null}
              {access === "edit" && canArchive ? (
                <FuiButton appearance="primary" onClick={() => setArchiveOpen(true)}>
                  회고 마감 및 프로젝트 보관
                </FuiButton>
              ) : null}
              <FuiButton appearance="subtle" icon={<Print16Regular />} onClick={() => window.print()}>
                인쇄
              </FuiButton>
            </div>
          }
        />
      </div>
      <div className={styles.body}>
        {access === "draft" ? <Caption1 className={styles.note}>진행 중인 플랜입니다. 회고는 초안으로 남습니다.</Caption1> : null}
        {access === "readonly" ? <Caption1 className={styles.note}>보관된 플랜입니다. 회고는 읽기만 할 수 있습니다.</Caption1> : null}
        {saved ? <Caption1 role="status">템플릿으로 저장했습니다.</Caption1> : null}

        <section className={styles.column} aria-label="스코어카드">
          <SectionTitle>스코어카드</SectionTitle>
          <div className={table.frame}>
            <table className={table.table}>
              <thead>
                <tr>
                  <th scope="col" className={table.th}>지표</th>
                  <th scope="col" className={mergeClasses(table.th, styles.value)}>값</th>
                </tr>
              </thead>
              <tbody>
                {score.metrics.map((row) => (
                  <tr key={row.id}>
                    <td className={table.td}>{row.label}</td>
                    <td className={mergeClasses(table.td, styles.value, row.unavailable && styles.quiet)}>{row.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.column} aria-label="핵심 개선 과제">
          <SectionTitle>핵심 개선 과제</SectionTitle>
          {promoted.length === 0 ? <Caption1 className={styles.quiet}>표가 있는 문제와 시도가 없습니다.</Caption1> : null}
          {promoted.map((card) => (
            <Caption1 key={card.id}>
              {card.column === "problem" ? "문제" : "시도"} · {card.body} · {card.votes.length}
            </Caption1>
          ))}
        </section>

        <section className={styles.column} aria-label="KPT">
          <SectionTitle>KPT</SectionTitle>
          <div className={styles.board}>
            {COLUMNS.map((column) => (
              <KptColumn
                key={column.id}
                styles={styles}
                label={column.label}
                column={column.id}
                cards={cards.filter((card) => card.column === column.id)}
                locked={locked}
              />
            ))}
          </div>
        </section>

        <label className={styles.summary}>
          <SectionTitle>PM 요약</SectionTitle>
          <Textarea
            aria-label="PM 요약"
            value={summary}
            resize="vertical"
            disabled={locked}
            onChange={(_, data) => setSummary(data.value)}
            onBlur={() => {
              if (locked) return;
              if ((project.retrospective?.summary ?? "") === summary.trim()) return;
              useProjectStore.getState().setRetrospectiveSummary(summary);
            }}
          />
        </label>
      </div>
      <ConfirmDialog
        open={archiveOpen}
        title="회고 마감 및 프로젝트 보관"
        message="회고를 마감하고 플랜을 보관할까요? 보관 후에는 읽기만 할 수 있습니다."
        confirmLabel="보관"
        onCancel={() => setArchiveOpen(false)}
        onConfirm={() => {
          useProjectStore.getState().setLifecycle("archived");
          setArchiveOpen(false);
        }}
      />
    </div>
  );
}

function KptColumn({
  styles,
  label,
  column,
  cards,
  locked,
}: {
  styles: ReturnType<typeof useStyles>;
  label: string;
  column: KptCard["column"];
  cards: KptCard[];
  locked: boolean;
}) {
  const [body, setBody] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const add = () => {
    useProjectStore.getState().addRetrospectiveCard({ column, body, anonymous });
    setBody("");
    setAnonymous(false);
  };
  return (
    <div className={styles.column}>
      <SectionTitle>{label}</SectionTitle>
      {cards.length === 0 ? <Caption1 className={styles.quiet}>없음</Caption1> : null}
      {cards.map((card) => (
        <article key={card.id} className={styles.card}>
          <span>{card.body}</span>
          <div className={styles.cardMeta}>
            <span>{kptAuthorLabel(card)}</span>
            <span className={styles.count}>{card.votes.length}</span>
            {locked ? null : (
              <FuiButton appearance="subtle" aria-label="찬성" onClick={() => useProjectStore.getState().upvoteRetrospectiveCard(card.id)}>
                +1
              </FuiButton>
            )}
          </div>
        </article>
      ))}
      {locked ? null : (
        <div className={styles.composer} data-print-hide="">
          <FuiInput aria-label={`${label} 카드`} value={body} placeholder="한 줄" onChange={(_, data) => setBody(data.value)} />
          <Checkbox checked={anonymous} label="익명" onChange={(_, data) => setAnonymous(!!data.checked)} />
          <FuiButton appearance="subtle" disabled={!body.trim()} onClick={add}>
            추가
          </FuiButton>
        </div>
      )}
    </div>
  );
}
